import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@nextui-org/react';
import { Icon } from '@iconify/react/offline';
import PhotoIcon from '@iconify-icons/tabler/photo';
import SpreadsheetIcon from '@iconify-icons/tabler/file-spreadsheet';
import PdfIcon from '@iconify-icons/tabler/file-type-pdf';
import WordIcon from '@iconify-icons/tabler/file-type-doc';
import MailIcon from '@iconify-icons/tabler/mail';
import TextIcon from '@iconify-icons/tabler/file-text';
import {
  getCaseDocumentUrl,
  type CaseDetail,
  type CaseEvent,
  type CaseFile,
} from 'common/src/api/shipment-cases';
import { useTranslation } from 'common/src/hooks/useTranslation';
import { useScopedRequest } from '../shipments/arrival-manager/use-scoped-request';
import { caseFileAppearance } from './model';

const attachmentIcons = {
  'tabler:photo': PhotoIcon,
  'tabler:file-spreadsheet': SpreadsheetIcon,
  'tabler:file-type-pdf': PdfIcon,
  'tabler:file-type-doc': WordIcon,
  'tabler:mail': MailIcon,
  'tabler:file-text': TextIcon,
};

export default function CaseAttachments({
  event,
  documents,
  identity,
}: {
  event: CaseEvent;
  documents: CaseDetail['documents'];
  identity: string;
}) {
  return (
    <div className='flex flex-wrap gap-3 pt-2'>
      {event.files.map((file, index) => {
        const doc = documents.find(
          (d) => d.event_id === event.id && d.file_index === index
        );
        return (
          <Attachment
            key={`${event.id}:${index}:${doc?.path || ''}`}
            file={file}
            path={doc?.path}
            identity={identity}
          />
        );
      })}
    </div>
  );
}
function Attachment({
  file,
  path,
  identity,
}: {
  file: CaseFile;
  path?: string;
  identity: string;
}) {
  const { t } = useTranslation('shipmentCases');
  const appearance = caseFileAppearance(file);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const scope = `${identity}:${path || ''}`;
  const active = useRef(scope);
  active.current = scope;
  useEffect(() => {
    active.current = scope;
    return () => {
      active.current = '';
    };
  }, [scope]);
  const previewKey =
    path && appearance.image ? `${identity}:preview:${path}` : null;
  const loadPreview = useCallback(
    () =>
      path ? getCaseDocumentUrl(path, file.name, false) : Promise.resolve(''),
    [path, file.name]
  );
  const preview = useScopedRequest(previewKey, loadPreview);
  async function open() {
    if (!path || opening) return;
    setOpening(true);
    setError(null);
    try {
      const url = await getCaseDocumentUrl(path, file.name, !appearance.image);
      if (active.current !== scope) return;
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } catch {
      if (active.current === scope)
        setError(t('No se pudo abrir el documento. Intenta nuevamente.'));
    } finally {
      if (active.current === scope) setOpening(false);
    }
  }
  return (
    <div className='flex max-w-full flex-col gap-1'>
      <Button
        variant='flat'
        className='h-auto max-w-64 justify-start gap-2 p-2'
        aria-label={`${t('Abrir archivo')}: ${file.name}`}
        isDisabled={!path}
        isLoading={opening}
        onPress={() => void open()}
      >
        {appearance.image && preview.data && !imageError ? (
          <img
            src={preview.data}
            alt={`${t('Vista previa')}: ${file.name}`}
            loading='lazy'
            referrerPolicy='no-referrer'
            className='h-12 w-16 shrink-0 rounded-medium object-cover'
            onError={() => setImageError(true)}
          />
        ) : (
          <span
            className={`flex size-9 shrink-0 items-center justify-center rounded-medium ${appearance.classes}`}
          >
            {preview.loading ? (
              <Spinner size='sm' />
            ) : (
              <Icon
                icon={
                  attachmentIcons[
                    appearance.icon as keyof typeof attachmentIcons
                  ]
                }
                width={22}
              />
            )}
          </span>
        )}
        <span className='flex min-w-0 flex-col gap-1 text-left'>
          <span className='whitespace-normal break-words text-xs'>
            {file.name}
          </span>
          <span className='text-xs text-default-500'>
            {path
              ? `${appearance.label} · ${Math.max(1, Math.ceil(file.size / 1024))} KB`
              : t('Archivo pendiente de subir')}
          </span>
        </span>
      </Button>
      {path && appearance.image && (preview.error || imageError) && (
        <Button
          size='sm'
          variant='light'
          onPress={() => {
            setImageError(false);
            void preview.reload();
          }}
        >
          {t('Reintentar vista previa')}
        </Button>
      )}
      {error && <p className='max-w-72 text-xs text-danger'>{error}</p>}
    </div>
  );
}
